import { view, blocks } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getParentBlockByElement from 'src/js/modules/blocks/helpers/get-parent-block-by-element';
import getPageSettingsTraits from 'src/js/modules/analytics/segment/helpers/get-page-settings-traits';
import websiteDesignTriggerIds from 'src/js/modules/website-design/constants/trigger-ids';

const {
  track,
  trackers: { trackEvent },
  constants: { events },
} = segment;

function onDialogMainAction({ dialogType, ...traits }) {
  switch (dialogType) {
    case dialogTypes.LINK_FORMATTING_DIALOG:
    case dialogTypes.EDIT_LINK_DIALOG:
    case dialogTypes.NAVIGATION_ITEM_SETTINGS_DIALOG:
    case dialogTypes.FOOTER_NAVIGATION_ITEM_SETTING_DIALOG: {
      const { elementId, customPath, ...restTraits } = traits;
      const element = view.accessors.getLiveElement(elementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.LINK_SETTINGS_DIALOG_SUBMITTED, {
        dialogId: dialogType,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
        downloadUrl: customPath,
        ...restTraits,
      });
      break;
    }
    case dialogTypes.FOOTER_NAVIGATION_SETTINGS_DIALOG: {
      const { elementId, ...restTraits } = traits;
      const element = view.accessors.getLiveElement(elementId);
      const blockElement = getParentBlockByElement(element);

      track(events.LIST_SETTINGS_DIALOG_SUBMITTED, {
        dialogId: dialogType,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
        ...restTraits,
      });
      break;
    }
    case dialogTypes.BLOCK_SETTINGS: {
      trackEvent(events.BLOCK_SETTINGS_DIALOG_SUBMITTED, {
        dialogId: dialogType,
        ...traits,
      });
      break;
    }
    case dialogTypes.ADD_WIDGET: {
      // This type of action is specific and its handling should be excluded from here.
      // https://github.com/yola/ws-editor/issues/3053
      break;
    }
    case dialogTypes.SELECT_ICON: {
      break;
    }
    case dialogTypes.WEBSITE_SETTINGS: {
      trackEvent(events.WEBSITE_SETTINGS_DIALOG_SUBMITTED, {
        ...traits,
      });

      break;
    }
    case dialogTypes.CONTACT_FORM_SETTINGS_DIALOG: {
      track(events.CONTACT_FORM_SETTINGS_DIALOG_SUBMITTED, {
        ...traits,
        dialogId: dialogType,
      });

      break;
    }
    case dialogTypes.CONTACT_FORM_EDIT_ITEM: {
      track(events.CONTACT_FORM_EDIT_ITEM_DIALOG_SUBMITTED, {
        ...traits,
        dialogId: dialogType,
      });
      break;
    }
    case dialogTypes.CONTACT_FORM_NEW_ITEM: {
      track(events.CONTACT_FORM_ADD_ITEM_DIALOG_SUBMITTED, {
        ...traits,
        dialogId: dialogType,
      });
      break;
    }
    case dialogTypes.CONTACT_FORM_DELETE_EMAIL_DIALOG: {
      track(events.CONTACT_FORM_DELETE_EMAIL_DIALOG_SUBMITTED, {
        ...traits,
        dialogId: dialogType,
      });
      break;
    }
    case dialogTypes.GALLERY_DIALOG: {
      const { elementId, ...restTraits } = traits;
      const element = view.accessors.getLiveElement(elementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.GALLERY_SETTINGS_SUBMITTED, {
        dialogId: dialogType,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        ...restTraits,
      });

      break;
    }
    case dialogTypes.SLIDER_SETTINGS_DIALOG: {
      const { elementId, autoChange, duration, ...restTraits } = traits;
      const element = view.accessors.getLiveElement(elementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.SLIDER_SETTINGS_SUBMITTED, {
        dialogId: dialogType,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        ...restTraits,
        autoChange,
        duration: autoChange ? duration : null,
        ...restTraits,
      });

      break;
    }
    case dialogTypes.DIFM_PROMO_BANNER_DIALOG: {
      trackEvent(events.DIFM_PROMO_YES_BUILD_A_WEBSITE_TRIGGER_CLICKED, traits);
      break;
    }
    case dialogTypes.DIFM_PROMO_PACKAGES_DIALOG: {
      const { triggerId, selectedService, selectedPackage } = traits;

      trackEvent(events.DIFM_PROMO_DIALOG_SUBMITTED, {
        dialogId: dialogType,
        triggerId,
        selectedService,
        selectedPackage,
      });

      break;
    }
    case dialogTypes.BLOCK_REPLACEMENT_DIALOG: {
      const { oldBlock, oldBlockVariationId, newBlock, newBlockVariationId } = traits;

      trackEvent(events.BLOCK_REPLACEMENT_DIALOG_SUBMITTED, {
        dialogId: dialogType,
        oldBlock,
        oldBlockVariationId,
        newBlock,
        newBlockVariationId,
      });

      break;
    }
    case dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE: {
      const { triggerId, colorsAdjusted } = traits;

      trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_SETTINGS_SUBMITTED, {
        dialogId: dialogType,
        triggerId,
        colorsAdjusted,
      });

      break;
    }
    case dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG: {
      const { colorScheme, advancedColorAdjusted } = traits;

      trackEvent(events.DESIGN_SETTINGS_COLOR_SCHEME_SETTINGS_SUBMITTED, {
        colorScheme,
        advancedColorAdjusted,
      });

      break;
    }
    case dialogTypes.ADVANCED_COLOR_PICKER_DIALOG: {
      const { colorAdjusted, variableId, variableCategory, colorType, gradientType, baseColor } =
        traits;

      trackEvent(events.DESIGN_SETTINGS_COLOR_SETTINGS_SUBMITTED, {
        dialogId: dialogType,
        colorAdjusted,
        variableId,
        variableCategory,
        colorType,
        gradientType,
        baseColor,
      });
      break;
    }

    case dialogTypes.BASE_COLORS_PICKER_DIALOG: {
      const { adjustedColors, triggerId, colorPreset } = traits;
      const event =
        triggerId === websiteDesignTriggerIds.ADD_CUSTOM_PALETTE
          ? events.DESIGN_SETTINGS_NEW_PALETTE_SUBMITTED
          : events.DESIGN_SETTINGS_EDIT_PALETTE_SUBMITTED;

      trackEvent(event, {
        adjustedColors,
        colorPreset,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_EFFECTS_DIALOG: {
      const { triggerId, effectsAdjusted, sourceId } = traits;

      trackEvent(events.DESIGN_SETTINGS_EFFECTS_DIALOG_SUBMITTED, {
        triggerId,
        sourceId,
        effectsAdjusted,
      });

      break;
    }

    case dialogTypes.DESIGN_SETTINGS_BUTTONS_DIALOG: {
      const { triggerId, buttonPresetAdjusted, sourceId, buttonPreset } = traits;

      trackEvent(events.DESIGN_SETTINGS_BUTTONS_DIALOG_SUBMITTED, {
        triggerId,
        buttonPresetAdjusted,
        buttonPreset,
        sourceId,
      });

      break;
    }

    case dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG: {
      const { triggerId, fontsAdjusted, sourceId, fontPreset } = traits;

      trackEvent(events.DESIGN_SETTINGS_FONTS_DIALOG_SUBMITTED, {
        fontsAdjusted,
        fontPreset,
        sourceId,
        triggerId,
      });

      break;
    }

    case dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG: {
      const { triggerId, colorPaletteAdjusted, sourceId, colorPreset } = traits;

      trackEvent(events.DESIGN_SETTINGS_COLORS_DIALOG_SUBMITTED, {
        triggerId,
        colorPaletteAdjusted,
        colorPreset,
        sourceId,
      });

      break;
    }

    case dialogTypes.BUTTON_PRESET_SETTINGS: {
      const { triggerId, buttonPreset } = traits;

      trackEvent(events.DESIGN_SETTINGS_BUTTON_PRESET_SETTINGS_SUBMITTED, {
        triggerId,
        buttonPreset,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_OTHER_STYLES_DIALOG: {
      trackEvent(events.DESIGN_SETTINGS_OTHER_STYLES_DIALOG_SUBMITTED, traits);
      break;
    }

    case dialogTypes.ADD_FONT_PAIR_DIALOG: {
      trackEvent(events.DESIGN_SETTINGS_ADD_NEW_FONT_PAIR_SUBMITTED, {
        dialogId: dialogType,
        ...traits,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG: {
      trackEvent(events.DESIGN_SETTINGS_FONT_SETTINGS_SUBMITTED, {
        dialogId: dialogType,
        ...traits,
      });
      break;
    }

    case dialogTypes.PAGE_SETTINGS: {
      const { displayInNavigation, ...pageSettings } = traits;

      trackEvent(events.PAGE_SETTINGS_SUBMITTED, {
        dialogId: dialogType,
        ...getPageSettingsTraits(pageSettings, { displayInNavigation }),
      });
      break;
    }

    case dialogTypes.SITE_WIZARD_SETUP_DIALOG:
    case dialogTypes.AI_ERROR_DIALOG:
    case dialogTypes.AI_TEXT_ASSISTANT_ERROR_DIALOG:
    case dialogTypes.SITE_WIZARD_CONFIRM_EXIT_DIALOG: {
      break;
    }

    case dialogTypes.AI_ONBOARDING_DIALOG: {
      trackEvent(events.AI_WIZARD_ONBOARDING_BUSINESS_INFO_ADDED, traits);
      break;
    }

    case dialogTypes.TRANSLATE_PAGE: {
      trackEvent(events.TRANSLATE_PAGE_DIALOG_SUBMITTED, traits);
      break;
    }

    case dialogTypes.CREATE_FOLDER_DIALOG: {
      const { triggerId, folderName } = traits;

      trackEvent(events.ADD_FOLDER_DIALOG_SUBMITTED, {
        triggerId,
        folderName,
      });
      break;
    }

    case dialogTypes.DELETE_PAGE:
    case dialogTypes.DELETE_FOLDER:
    case dialogTypes.BULK_EDITING_DELETE_DIALOG: {
      const { itemType, bulkEditing, itemsCount } = traits;
      trackEvent(events.PAGE_MANAGER_DELETE_DIALOG_SUBMITTED, {
        itemType,
        bulkEditing,
        itemsCount,
      });
      break;
    }

    case dialogTypes.BULK_EDITING_MOVE_TO_DIALOG: {
      const { bulkEditing, itemsCount } = traits;
      trackEvent(events.PAGE_MANAGER_MOVE_TO_DIALOG_SUBMITTED, {
        bulkEditing,
        itemsCount,
      });
      break;
    }

    case dialogTypes.FOLDER_SETTINGS: {
      const { folderName } = traits;

      trackEvent(events.FOLDER_SETTINGS_DIALOG_SUBMITTED, {
        folderName,
      });
      break;
    }

    case dialogTypes.ADD_PAGE_DIALOG:
    case dialogTypes.NEW_PAGE_DIALOG: {
      const {
        pageTemplateId,
        pageName,
        displayInNavigation,
        generateWithAi = false,
        recipeId = null,
        userInput = null,
      } = traits;

      trackEvent(events.ADD_PAGE_DIALOG_SUBMITTED, {
        pageTemplateId,
        pageName,
        displayInNavigation,
        generateWithAi,
        recipeId,
        userInput,
      });

      break;
    }

    case dialogTypes.EMBED_HTML_SETTINGS_DIALOG: {
      const { renderInEditor } = traits;

      trackEvent(events.CUSTOM_HTML_DIALOG_SUBMITTED, {
        renderInEditor,
      });

      break;
    }

    case dialogTypes.TEXT_CUSTOM_SPACING_DIALOG: {
      const { targetElement, topParagraphSpacing, bottomParagraphSpacing, customLineHeight } =
        traits;
      const blockElement = getParentBlockByElement(targetElement);
      const blockId = blocks.accessors.getBlockIdByElement(blockElement);
      const blockVariationId = blocks.accessors.getVariationIdByElement(blockElement);

      trackEvent(events.TEXT_EDITING_CUSTOM_SPACING_DIALOG_SUBMITTED, {
        blockId,
        blockVariationId,
        topParagraphSpacing,
        bottomParagraphSpacing,
        customLineHeight,
      });
      break;
    }

    case dialogTypes.ADD_LANGUAGE: {
      const { targetLocale, aiTranslation } = traits;

      trackEvent(events.TRANSLATE_WEBSITE_DIALOG_SUBMITTED, {
        targetLocale,
        aiTranslation,
      });
      break;
    }

    default: {
      trackEvent(events.DIALOG_SUBMITTED, {
        dialogId: dialogType,
      });
    }
  }
}

export default onDialogMainAction;
